import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "./../../axios";
import { timeHelper, dateHelper } from "../../Utils/datetime_helper";
import { toast } from "react-toastify";
import {
  BookType,
  Calendar,
  ClockFadingIcon,
  Trophy,
  FileText,
  MessageSquare,
  Layers,
  Target,
  ArrowLeft,
  Save,
  Check,
  AlertCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";

function CompetencyEvalReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { competencyDetails } = location.state || {};

  const [isInProgress, setIsInProgress] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [totalStudents, setTotalStudents] = useState(0);
  const [processedStudents, setProcessedStudents] = useState(0);

  // NEW STATES FOR DUPLICATE CHECK
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(true);
  const [existingEvaluation, setExistingEvaluation] = useState(null);
  const [duplicateMessage, setDuplicateMessage] = useState("");

  const schoolId = JSON.parse(localStorage.getItem("schoolId"));
  const teacherId = JSON.parse(localStorage.getItem("Teacher_id"));

  let timeTableSessionId = JSON.parse(
    localStorage.getItem("timeTableSessionId"),
  );
  const academicSessionId = JSON.parse(
    localStorage.getItem("academicYearSessionId"),
  );

  const [studentList, setStudentList] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);

  // ─── Extract Details from State ──────────
  const standardId = competencyDetails?.standard_id?.id;
  const sectionId = competencyDetails?.section_id?.id;
  const assessmentEventId = competencyDetails?.assessmentEvent_id?.id; // 👈 renamed
  const domainId = competencyDetails?.domain_id?.id;
  const competencyId = competencyDetails?.competency_id?.id;
  const rubricScaleId = competencyDetails?.rubricScaleId;
  const rubricDetails = competencyDetails?.rubricScaleDetails;

  // Evaluation type from framework mapping
  const evaluationType =
    competencyDetails?.evaluationType?.name ||
    (rubricDetails ? "Rubrics + Remarks" : "Remarks");

  const evaluationTypeId = competencyDetails?.evaluationType?._id;

  const hasRubrics =
    evaluationType?.toLowerCase().includes("rubrics") && rubricDetails;
  const hasRemarks = evaluationType?.toLowerCase().includes("remarks");

  const rubricItems = rubricDetails?.rubricItems || [];

  // ─── CHECK FOR DUPLICATE EVALUATION ON MOUNT ──────────
  useEffect(() => {
    const checkForDuplicate = async () => {
      if (
        !schoolId ||
        !standardId ||
        !sectionId ||
        !competencyId ||
        !assessmentEventId // 👈 changed
      ) {
        setIsCheckingDuplicate(false);
        return;
      }

      setIsCheckingDuplicate(true);
      try {
        console.log("🔵 Checking for existing evaluation...");

        const res = await axios.get(
          `/api/v1/competency/evaluations?schoolId=${schoolId}`,
        );

        const evaluations = res?.data?.data || [];
        console.log("All evaluations:", evaluations);

        // Find matching evaluation based on ALL criteria
        const matchingEvaluation = evaluations.find((evalItem) => {
          const evalStandardId =
            evalItem.standardId?._id || evalItem.standardId;
          const evalSectionId = evalItem.sectionId?._id || evalItem.sectionId;
          const evalCompetencyId =
            evalItem.competencyId?._id || evalItem.competencyId;
          // 👈 support both old (reportCycleId) and new (assessmentEventId) fields
          const evalAssessmentEventId =
            evalItem.assessmentEventId?._id ||
            evalItem.assessmentEventId ||
            evalItem.reportCycleId?._id ||
            evalItem.reportCycleId;

          return (
            evalStandardId === standardId &&
            evalSectionId === sectionId &&
            evalCompetencyId === competencyId &&
            evalAssessmentEventId === assessmentEventId
          );
        });

        if (matchingEvaluation) {
          console.log("✅ Found existing evaluation:", matchingEvaluation);
          setExistingEvaluation(matchingEvaluation);
          setDuplicateMessage(
            `This competency evaluation already exists as "${matchingEvaluation.status.toUpperCase()}". 
          \nPlease check the existing record instead of creating a new one.`,
          );
          toast.info(
            `📋 Duplicate found! Status: ${matchingEvaluation.status.toUpperCase()}`,
            { autoClose: 4000 },
          );
        } else {
          console.log("❌ No duplicate found");
          setExistingEvaluation(null);
          setDuplicateMessage("");
        }
      } catch (error) {
        console.error("❌ Error checking duplicates:", error);
        setExistingEvaluation(null);
        setDuplicateMessage("");
      } finally {
        setIsCheckingDuplicate(false);
      }
    };

    checkForDuplicate();
  }, [schoolId, standardId, sectionId, competencyId, assessmentEventId]); // 👈 changed

  // ─── Fetch & Sort Student List ──────────
  useEffect(() => {
    const getStudentList = async () => {
      if (!timeTableSessionId || !standardId || !sectionId) return;
      try {
        let res = await axios.get(
          `/api/v1/school/student/getList?standardId=${standardId}&sectionId=${sectionId}&timetableSession=${timeTableSessionId}`,
        );

        const students = (res?.data?.data || [])
          .map((student) => ({
            id: student.id,
            name: student.name,
            rollNumber: student.code || student.roll_number || "-",
            rubricValue: "",
            remark: "",
          }))
          .sort((a, b) => {
            const numA = parseInt(a.rollNumber, 10) || 0;
            const numB = parseInt(b.rollNumber, 10) || 0;
            return numA - numB;
          });

        setStudentList(students);
        setTotalStudents(students.length);
      } catch (error) {
        console.log("Error fetching students:", error);
        toast.error("Failed to load students");
      }
    };
    getStudentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standardId, sectionId]);

  // ─── Handle Rubric Change ──────────
  const handleRubricChange = (index, value) => {
    const updatedList = [...studentList];
    updatedList[index].rubricValue = value;
    setStudentList(updatedList);

    const studentId = updatedList[index].id;
    const existingIndex = selectedStudents.findIndex((s) => s.id === studentId);

    if (existingIndex !== -1) {
      const updatedSelected = [...selectedStudents];
      updatedSelected[existingIndex].rubricValue = value;
      setSelectedStudents(updatedSelected);
    } else {
      setSelectedStudents((prev) => [
        ...prev,
        {
          id: studentId,
          rubricValue: value,
          remark: updatedList[index].remark,
        },
      ]);
    }
  };

  // ─── Handle Remark Change ──────────
  const handleRemarkChange = (index, value) => {
    const updatedList = [...studentList];
    updatedList[index].remark = value;
    setStudentList(updatedList);

    const studentId = updatedList[index].id;
    const existingIndex = selectedStudents.findIndex((s) => s.id === studentId);

    if (existingIndex !== -1) {
      const updatedSelected = [...selectedStudents];
      updatedSelected[existingIndex].remark = value;
      setSelectedStudents(updatedSelected);
    } else {
      setSelectedStudents((prev) => [
        ...prev,
        {
          id: studentId,
          rubricValue: updatedList[index].rubricValue,
          remark: value,
        },
      ]);
    }
  };

  // ─── Validate before submit ──────────
  const validateSubmission = () => {
    if (hasRubrics) {
      const missing = studentList.filter((s) => !s.rubricValue);
      if (missing.length > 0) {
        toast.warning(
          `Please select rubric for all students (${missing.length} remaining)`,
        );
        return false;
      }
    }
    return true;
  };

  // ─── Handle Submission ──────────
  const handleSubmission = async (status) => {
    // if (!validateSubmission()) return;

    setIsInProgress(true);
    setDisableButton(true);
    setShowLoadingDialog(true);
    setLoadingMessage("Initializing evaluation...");
    setLoadingProgress(5);
    setProcessedStudents(0);

    if (!studentList || studentList.length === 0) {
      toast.warning("No students found to evaluate.");
      setIsInProgress(false);
      setDisableButton(false);
      setShowLoadingDialog(false);
      return;
    }

    const payload = {
      name: competencyDetails?.name || "Untitled Evaluation",
      description: competencyDetails?.description || "",
      schoolId: schoolId,
      standardId: standardId,
      sectionId: sectionId,
      assessmentEventId: assessmentEventId, // 👈 changed from reportCycleId
      domainId: domainId,
      competencyId: competencyId,
      rubricScaleId: rubricScaleId,
      academicYearSessionId: academicSessionId,
      timetableSessionId: timeTableSessionId,
      status: status === "submit" ? "completed" : "draft",
      evaluationTypeId: evaluationTypeId,
      teacherId: teacherId,
    };

    let evaluationId = null;

    try {
      // Step 1: Create Evaluation
      setLoadingProgress(20);
      setLoadingMessage("Creating evaluation record...");

      const res = await axios.post(`/api/v1/competency/evaluations`, payload);

      if (!res?.data?.success) {
        throw new Error(
          res?.data?.message || "Failed to create evaluation record.",
        );
      }

      evaluationId = res?.data?.data?._id || res?.data?.data?.id;
      if (!evaluationId) {
        throw new Error("Evaluation created but no ID returned.");
      }

      // Step 2: Add Students to Evaluation
      setLoadingProgress(35);
      setLoadingMessage(
        `Adding ${studentList.length} students to evaluation...`,
      );

      const studentIds = studentList.map((student) => student.id);

      const addStudentsPayload = {
        schoolId: schoolId,
        studentIds: studentIds,
      };

      await axios.post(
        `/api/v1/competency/evaluations/${evaluationId}/students/add`,
        addStudentsPayload,
      );

      // Step 3: Prepare Bulk Payload
      setLoadingProgress(55);
      setLoadingMessage("Preparing student evaluations...");

      const finalTeacherId =
        teacherId || JSON.parse(localStorage.getItem("Teacher_id")) || null;

      const studentEntriesPayload = {
        teacherId: finalTeacherId,
        isSubmitComplete: status === "submit",
        studentEntries: studentList.map((student) => ({
          studentId: student.id,
          rubricValue: hasRubrics ? student.rubricValue || "" : undefined,
          remark: hasRemarks ? student.remark || "" : undefined,
        })),
      };

      // Step 4: Send Bulk Student Entries
      setLoadingProgress(75);
      setLoadingMessage(
        `Saving evaluations for ${studentList.length} students...`,
      );

      await axios.post(
        `/api/v1/competency/evaluations/${evaluationId}/student-entries`,
        studentEntriesPayload,
      );

      // Step 5: Finalize
      setLoadingProgress(100);
      setLoadingMessage("Finalizing submission...");
      await new Promise((resolve) => setTimeout(resolve, 600));

      setShowLoadingDialog(false);
      toast.success(
        status === "submit"
          ? "✅ Competency evaluation submitted successfully!"
          : "💾 Draft saved successfully!",
        { autoClose: 3000 },
      );

      localStorage.removeItem("addCompetencyDraft");
      navigate("/offline-performance/competency");
    } catch (error) {
      console.error("🚨 Submission Error:", error);

      if (evaluationId) {
        try {
          setLoadingMessage("Rolling back changes...");
          await axios.delete(`/api/v1/competency/evaluations/${evaluationId}`);
        } catch (rollbackError) {
          console.error(
            "❌ Failed to delete orphaned evaluation:",
            rollbackError,
          );
        }
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong while saving. Please try again.";

      toast.error(`❌ ${errorMessage}`, { autoClose: 5000 });

      setIsInProgress(false);
      setDisableButton(false);
      setShowLoadingDialog(false);
    } finally {
      setIsInProgress(false);
      setDisableButton(false);
    }
  };

  // ─── Render Rubric Dropdown ──────────
  const renderRubricDropdown = (index, student) => {
    return (
      <div style={{ width: "100%" }}>
        <select
          className="form-select"
          value={student.rubricValue || ""}
          onChange={(e) => handleRubricChange(index, e.target.value)}
          style={{
            width: "100%",
            padding: "8px 10px",
            borderRadius: "8px",
            border: "1.5px solid #e2e8f0",
            fontSize: "13px",
            backgroundColor: "#fff",
            cursor: "pointer",
            minHeight: "40px",
            color: student.rubricValue ? "#1e293b" : "#94a3b8",
          }}
        >
          <option value="">Select Rubric Level</option>
          {rubricItems.map((item, idx) => (
            <option key={idx} value={item.value}>
              {item.value}
            </option>
          ))}
        </select>
        {student.rubricValue && (
          <Typography
            sx={{
              fontSize: "11px",
              color: "#64748b",
              mt: 0.5,
              fontStyle: "italic",
            }}
          >
            {
              rubricItems.find((r) => r.value === student.rubricValue)
                ?.description
            }
          </Typography>
        )}
      </div>
    );
  };

  // ─── Render Remarks Input ──────────
  const renderRemarksInput = (index, student) => {
    return (
      <textarea
        rows="1"
        value={student.remark || ""}
        onChange={(e) => handleRemarkChange(index, e.target.value)}
        placeholder="Enter remark..."
        maxLength={250}
        style={{
          width: "100%",
          padding: "8px 10px",
          borderRadius: "8px",
          border: "1.5px solid #e2e8f0",
          fontSize: "13px",
          resize: "vertical",
          minHeight: "40px",
          backgroundColor: "#fff",
          fontFamily: "inherit",
        }}
      />
    );
  };

  const handleNavigate = (status) => {
    localStorage.removeItem("addCompetencyDraft");
    if (status === "draft") {
      navigate("/draft-competency-report", {
        state: {
          assessmentId: existingEvaluation._id,
          assessment: existingEvaluation,
        },
      });
    }
    if (status === "completed") {
      navigate("/completed-competency-report", {
        state: {
          assessmentId: existingEvaluation._id,
          assessment: existingEvaluation,
        },
      });
    }
  };

  // ─── Get column count for empty state ──────────
  const getColumnCount = () => {
    let count = 2;
    if (hasRubrics) count++;
    if (hasRemarks) count++;
    return count;
  };

  // ─── LOADING STATE FOR DUPLICATE CHECK ──────────
  if (isCheckingDuplicate) {
    return (
      <div
        className="w-100 d-flex justify-content-center align-items-center"
        style={{ minHeight: "300px" }}
      >
        <div className="d-flex flex-column align-items-center gap-2">
          <div className="spinner-border text-warning" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="text-muted small">
            Checking for existing records...
          </span>
        </div>
      </div>
    );
  }

  // ─── DUPLICATE FOUND UI ──────────
  if (existingEvaluation) {
    return (
      <div className="w-100" style={{ minHeight: "calc(100vh - 100px)" }}>
        <div className="mb-3 pt-3">
          <div
            className="d-flex align-items-center gap-2 cursor-pointer"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/add-competency")}
          >
            <div>
              <ArrowLeft size={20} color="#64748b" />
            </div>
            <div className="d-flex flex-column gap-1">
              <span
                className="head_manual"
                style={{ fontSize: "18px", fontWeight: 600 }}
              >
                Competency Evaluation
              </span>
              <span
                className="fw-small text-muted"
                style={{ fontSize: "10px" }}
              >
                Dashboard / Performance / Competency Evaluation
              </span>
            </div>
          </div>
        </div>

        <div
          className="w-100 py-5 px-4 rounded-3 d-flex flex-column align-items-center justify-content-center"
          style={{
            backgroundColor: "#fff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            minHeight: "400px",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              backgroundColor: "#fef3c7",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px",
              color: "#d97706",
            }}
          >
            <AlertCircle size={40} />
          </div>

          <h4
            className="fw-bold"
            style={{ color: "#1f2937", marginBottom: "10px" }}
          >
            Evaluation Already Exists
          </h4>

          <p
            style={{
              color: "#6b7280",
              textAlign: "center",
              maxWidth: "500px",
              fontSize: "15px",
            }}
          >
            A competency evaluation for{" "}
            <strong>
              {competencyDetails?.standard_id?.name} -{" "}
              {competencyDetails?.section_id?.name}
            </strong>
            with competency{" "}
            <strong>"{competencyDetails?.competency_id?.name}"</strong> already
            exists.
            <br />
            <br />
            <strong>Current Status:</strong>{" "}
            <span
              style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "20px",
                fontSize: "13px",
                fontWeight: 600,
                backgroundColor:
                  existingEvaluation.status === "completed"
                    ? "#d1fae5"
                    : "#fef3c7",
                color:
                  existingEvaluation.status === "completed"
                    ? "#065f46"
                    : "#92400e",
              }}
            >
              {existingEvaluation.status.toUpperCase()}
            </span>
          </p>

          <div className="d-flex gap-3 mt-3">
            <button
              className="btn"
              onClick={() => navigate("/add-competency")}
              style={{
                padding: "10px 24px",
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                backgroundColor: "#fff",
                color: "#4b5563",
                fontWeight: 600,
              }}
            >
              Go Back
            </button>
            <button
              className="btn"
              onClick={() => {
                handleNavigate(existingEvaluation.status);
              }}
              style={{
                padding: "10px 24px",
                borderRadius: "10px",
                border: "none",
                backgroundColor: "#f77f00",
                color: "#fff",
                fontWeight: 600,
              }}
            >
              View Existing Record
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── NORMAL UI ──────────
  return (
    <div
      className=" w-100  orange-scrollBar"
      style={{
        overflowY: "hidden",
        height: "calc(100vh - 100px)",
      }}
    >
      {/* ─── Loading Dialog ────────── */}
      <Dialog
        open={showLoadingDialog}
        disableEscapeKeyDown
        PaperProps={{
          style: {
            borderRadius: "20px",
            padding: "0",
            minWidth: "420px",
            maxWidth: "460px",
            boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              height: "4px",
              width: "100%",
              background:
                "linear-gradient(90deg, #f97316, #fb923c, #fbbf24, #f97316)",
              backgroundSize: "300% 100%",
              animation: "shimmer 2s infinite linear",
            }}
          />
          <Box sx={{ textAlign: "center", py: 1, px: 1 }}>
            <Box sx={{ position: "relative", display: "inline-flex", mb: 1 }}>
              <CircularProgress
                variant="determinate"
                value={loadingProgress}
                size={80}
                thickness={3.5}
                sx={{
                  color: "#f97316",
                  "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#f97316",
                    fontSize: "18px",
                    lineHeight: 1,
                  }}
                >
                  {loadingProgress}%
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                mb: 0.5,
                fontSize: "17px",
              }}
            >
              {loadingMessage || "Processing..."}
            </Typography>

            {totalStudents > 0 && (
              <Typography
                variant="body2"
                sx={{ color: "#64748b", fontSize: "13px", mb: 3 }}
              >
                {processedStudents} of {totalStudents} students processed
              </Typography>
            )}

            <Box sx={{ width: "100%", mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 8,
                  borderRadius: "10px",
                  backgroundColor: "#f1f5f9",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: "10px",
                    background: "linear-gradient(90deg, #f97316, #fb923c)",
                    transition: "transform 0.4s ease-in-out",
                  },
                }}
              />
            </Box>

            <Box
              sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}
            >
              {["Save", "Students", "Results", "Done"].map((step, idx) => {
                const stepProgress = (idx + 1) * 25;
                const isActive = loadingProgress >= stepProgress;
                const isCurrent =
                  loadingProgress >= idx * 25 && loadingProgress < stepProgress;
                return (
                  <Box
                    key={step}
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: isActive
                          ? isCurrent
                            ? "#f97316"
                            : "#22c55e"
                          : "#e2e8f0",
                        transition: "all 0.3s ease",
                        boxShadow: isCurrent
                          ? "0 0 0 4px rgba(249, 115, 22, 0.2)"
                          : "none",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "11px",
                        color: isActive ? "#1e293b" : "#94a3b8",
                        fontWeight: isActive ? 500 : 400,
                      }}
                    >
                      {step}
                    </Typography>
                    {idx < 3 && (
                      <Box
                        sx={{
                          width: 16,
                          height: 1,
                          backgroundColor: isActive ? "#cbd5e1" : "#e2e8f0",
                          mx: 0.5,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ─── Header ────────── */}
      <div className="mb-3 pt-3">
        <div
          className="d-flex align-items-center gap-2 cursor-pointer"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/add-competency")}
        >
          <div>
            <ArrowLeft size={20} color="#64748b" />
          </div>
          <div className="d-flex flex-column gap-1">
            <span
              className="head_manual"
              style={{ fontSize: "18px", fontWeight: 600 }}
            >
              Competency Evaluation
            </span>
            <span className="fw-small text-muted" style={{ fontSize: "10px" }}>
              Dashboard / Performance / Competency Evaluation
            </span>
          </div>
        </div>
      </div>

      {/* ─── Main Card ────────── */}
      <div
        className="w-100 py-3 px-4 rounded-3"
        style={{
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 190px)",
        }}
      >
        {/* ─── Top Info Bar ────────── */}
        <div style={{ flexShrink: 0 }}>
          <div className="class-section-subject d-flex align-items-start flex-column gap-2 ml-3">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div
                className="fs-5 poppins-semibold"
                style={{ color: "#1e293b" }}
              >
                <span>
                  {competencyDetails?.standard_id?.name} {" - "}
                  {competencyDetails?.section_id?.name}
                </span>
              </div>

              <span
                className="badge"
                style={{
                  backgroundColor: "#6366f1",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 12px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Layers size={12} />
                {competencyDetails?.domain_id?.name || "Domain"}
              </span>

              <span
                className="badge"
                style={{
                  backgroundColor: "#8b5cf6",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 12px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Target size={12} />
                {competencyDetails?.competency_id?.name || "Competency"}
              </span>

              <span
                className="badge"
                style={{
                  backgroundColor: "#f77f00",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 12px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <Trophy size={12} />
                {rubricDetails?.name || "Rubric"}
              </span>

              <span
                className="badge"
                style={{
                  backgroundColor:
                    hasRubrics && hasRemarks
                      ? "#0ea5e9"
                      : hasRubrics
                        ? "#10b981"
                        : "#f59e0b",
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "5px 12px",
                  borderRadius: "20px",
                }}
              >
                {evaluationType}
              </span>
            </div>

            <div className="d-flex align-items-center w-100 gap-4 flex-wrap mt-1">
              <div
                className="title-text"
                style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}
              >
                <BookType
                  size={16}
                  className="me-2"
                  style={{ color: "#f77f00" }}
                />
                {competencyDetails?.name}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Calendar size={16} style={{ color: "#64748b" }} />
                <span
                  className="poppins-semibold"
                  style={{ fontSize: "13px", color: "#475569" }}
                >
                  {dateHelper(competencyDetails?.schedule_date)}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <ClockFadingIcon size={16} style={{ color: "#64748b" }} />
                <span
                  className="poppins-semibold"
                  style={{ fontSize: "13px", color: "#475569" }}
                >
                  {timeHelper(competencyDetails?.schedule_time)}
                </span>
              </div>
              <div className="d-flex align-items-center gap-2">
                <FileText size={16} style={{ color: "#64748b" }} />
                <span
                  className="poppins-semibold"
                  style={{ fontSize: "13px", color: "#475569" }}
                >
                  {/* 👈 Assessment Event name */}
                  {competencyDetails?.assessmentEvent_id?.name ||
                    "Assessment Event"}
                </span>
              </div>
            </div>
          </div>

          {competencyDetails?.description && (
            <div className="ml-3 mt-2">
              <div style={{ fontSize: "13px", color: "#64748b" }}>
                <span style={{ fontWeight: 600, color: "#334155" }}>
                  Description:
                </span>{" "}
                {competencyDetails.description}
              </div>
            </div>
          )}
        </div>

        {/* ─── Student Table ────────── */}
        <div
          className="mid-student-table"
          style={{ flex: 1, overflowY: "auto", minHeight: 0 }}
        >
          <div className="table-container-offlineSub">
            <div className="scrollable-table-wrapper">
              <table className="student-table-offlineSub">
                <thead style={{ position: "sticky", top: "0", zIndex: 10 }}>
                  <tr>
                    <th style={{ width: "25%" }}>Name</th>
                    <th style={{ width: "15%" }}>Roll No.</th>
                    {hasRubrics && (
                      <th style={{ width: hasRemarks ? "30%" : "50%" }}>
                        <div className="d-flex align-items-center gap-2">
                          <Trophy size={14} />
                          Rubric Level
                        </div>
                        <div className="sub-header-offlineSub">
                          <div
                            className="heading-text"
                            style={{ fontSize: "11px", color: "#94a3b8" }}
                          >
                            {rubricDetails?.name}
                          </div>
                        </div>
                      </th>
                    )}
                    {hasRemarks && (
                      <th style={{ width: hasRubrics ? "30%" : "60%" }}>
                        <div className="d-flex align-items-center gap-2">
                          <MessageSquare size={14} />
                          Remarks
                        </div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {studentList.map((student, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: 500, color: "#1e293b" }}>
                        {student.name}
                      </td>
                      <td style={{ color: "#64748b", fontSize: "13px" }}>
                        {student.rollNumber}
                      </td>
                      {hasRubrics && (
                        <td>{renderRubricDropdown(index, student)}</td>
                      )}
                      {hasRemarks && (
                        <td>{renderRemarksInput(index, student)}</td>
                      )}
                    </tr>
                  ))}
                  {studentList.length === 0 && (
                    <tr>
                      <td
                        colSpan={getColumnCount()}
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          color: "#94a3b8",
                        }}
                      >
                        No students found for this class and section.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ─── Action Buttons ────────── */}
        <div
          className="d-flex justify-content-end gap-3 mt-1"
          style={{
            flexShrink: 0,
            backgroundColor: "#fff",
            paddingTop: "12px",
            borderTop: "1px solid #f1f5f9",
          }}
        >
          <button
            className="btn d-flex align-items-center justify-content-center gap-2"
            onClick={() => handleSubmission("save")}
            disabled={isInProgress || disableButton}
            style={{
              backgroundColor: "#fff",
              color: "#475569",
              border: "2px solid #e2e8f0",
              width: "140px",
              height: "44px",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "14px",
              transition: "all 0.2s ease",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            }}
            onMouseEnter={(e) => {
              e.target.style.borderColor = "#f77f00";
              e.target.style.color = "#f77f00";
              e.target.style.backgroundColor = "#fff7ed";
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.color = "#475569";
              e.target.style.backgroundColor = "#fff";
            }}
          >
            <Save size={16} />
            Save Draft
          </button>
          <button
            className="btn d-flex align-items-center justify-content-center gap-2"
            onClick={() => handleSubmission("submit")}
            disabled={isInProgress || disableButton}
            style={{
              backgroundColor:
                isInProgress || disableButton ? "#cbd5e1" : "#f77f00",
              color: "white",
              width: "140px",
              height: "44px",
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: "14px",
              border: "none",
              boxShadow:
                isInProgress || disableButton
                  ? "none"
                  : "0 4px 14px rgba(247, 127, 0, 0.35)",
              transition: "all 0.2s ease",
              cursor: isInProgress || disableButton ? "not-allowed" : "pointer",
            }}
            onMouseEnter={(e) => {
              if (!isInProgress && !disableButton) {
                e.target.style.backgroundColor = "#e06b00";
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 6px 20px rgba(247, 127, 0, 0.45)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isInProgress && !disableButton) {
                e.target.style.backgroundColor = "#f77f00";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 14px rgba(247, 127, 0, 0.35)";
              }
            }}
          >
            <Check size={16} strokeWidth={3} />
            Submit
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: 200% 0;
          }
          100% {
            background-position: -200% 0;
          }
        }
      `}</style>
    </div>
  );
}

export default CompetencyEvalReport;
